import { onMounted, onUnmounted } from 'vue'

export function useTableInteraction() {
  let isMouseDown = false
  let isDragging = false

  // 检查元素是否在表格或表格手柄内部
  const isWithinTableElements = (target: Element | null): HTMLElement | null => {
    if (!target) return null

    // 向上遍历DOM树，查找表格元素
    let currentElement = target as HTMLElement
    while (currentElement && currentElement !== document.body) {
      // 检查是否是表格元素或表格手柄
      if (
        currentElement.classList.contains('hiprint-printElement-table') ||
        currentElement.classList.contains('hiprint-printElement-table-handle')
      ) {
        // 如果是手柄，返回父级表格元素
        if (currentElement.classList.contains('hiprint-printElement-table-handle')) {
          const tableElement = currentElement.closest('.hiprint-printElement-table')
          return tableElement as HTMLElement
        }

        // 如果是表格元素，直接返回
        return currentElement
      }

      currentElement = currentElement.parentElement as HTMLElement
    }

    return null
  }

  // 检查元素是否在打印纸张区域内部
  const isWithinPrintPaper = (target: Element | null): boolean => {
    if (!target) return false

    // 向上遍历DOM树，查找打印纸张元素
    let currentElement = target as HTMLElement
    while (currentElement && currentElement !== document.body) {
      if (currentElement.classList.contains('hiprint-printPaper')) {
        return true
      }
      currentElement = currentElement.parentElement as HTMLElement
    }

    return false
  }

  // 添加table-active类名
  const addTableActiveClass = (tableElement: HTMLElement) => {
    tableElement.classList.add('table-active')
  }

  // 移除所有table-active类名
  const removeAllTableActiveClass = () => {
    const activeTables = document.querySelectorAll('.hiprint-printElement-table.table-active')
    activeTables.forEach((table) => {
      table.classList.remove('table-active')
    })
  }

  // 鼠标按下事件处理
  const handleMouseDown = (event: MouseEvent) => {
    isMouseDown = true
    isDragging = false

    const tableElement = isWithinTableElements(event.target as Element)

    if (tableElement) {
      // 先移除所有活跃状态
      removeAllTableActiveClass()
      // 为当前表格添加活跃状态
      addTableActiveClass(tableElement)
    } else {
      // 点击在表格外部，检查是否在打印纸张区域内
      const isInPrintPaper = isWithinPrintPaper(event.target as Element)
      if (isInPrintPaper) {
        // 在打印纸张区域内但不在表格内，移除所有活跃状态
        removeAllTableActiveClass()
      }
      // 如果不在打印纸张区域内，则无需取消活跃状态
    }
  }

  // 鼠标移动事件处理
  const handleMouseMove = (event: MouseEvent) => {
    if (isMouseDown) {
      isDragging = true

      const tableElement = isWithinTableElements(event.target as Element)

      if (tableElement) {
        // 拖拽在表格内部，确保表格保持活跃状态
        if (!tableElement.classList.contains('table-active')) {
          removeAllTableActiveClass()
          addTableActiveClass(tableElement)
        }
      }
    }
  }

  // 鼠标松开事件处理
  const handleMouseUp = (event: MouseEvent) => {
    if (isMouseDown) {
      isMouseDown = false

      if (isDragging) {
        // 如果是拖拽操作，检查最终位置
        const tableElement = isWithinTableElements(event.target as Element)

        if (tableElement) {
          // 拖拽结束在表格内部，保持活跃状态
          if (!tableElement.classList.contains('table-active')) {
            removeAllTableActiveClass()
            addTableActiveClass(tableElement)
          }
        } else {
          // 拖拽结束在表格外部，检查是否在打印纸张区域内
          const isInPrintPaper = isWithinPrintPaper(event.target as Element)
          if (isInPrintPaper) {
            // 在打印纸张区域内但不在表格内，移除所有活跃状态
            removeAllTableActiveClass()
          }
          // 如果不在打印纸张区域内，则无需取消活跃状态
        }
      }

      isDragging = false
    }
  }

  // 点击事件处理
  const handleClick = (event: MouseEvent) => {
    const tableElement = isWithinTableElements(event.target as Element)

    if (tableElement) {
      // 点击在表格内部，添加活跃状态
      removeAllTableActiveClass()
      addTableActiveClass(tableElement)
    } else {
      // 点击在表格外部，检查是否在打印纸张区域内
      const isInPrintPaper = isWithinPrintPaper(event.target as Element)
      if (isInPrintPaper) {
        // 在打印纸张区域内但不在表格内，移除所有活跃状态
        removeAllTableActiveClass()
      }
      // 如果不在打印纸张区域内，则无需取消活跃状态
    }
  }

  // 初始化事件监听
  const initEventListeners = () => {
    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.addEventListener('click', handleClick)
  }

  // 移除事件监听
  const removeEventListeners = () => {
    document.removeEventListener('mousedown', handleMouseDown)
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
    document.removeEventListener('click', handleClick)
  }

  // 组件挂载时初始化
  onMounted(() => {
    initEventListeners()
  })

  // 组件卸载时清理
  onUnmounted(() => {
    removeEventListeners()
    // 清理所有活跃状态
    removeAllTableActiveClass()
  })

  return {
    initEventListeners,
    removeEventListeners,
    removeAllTableActiveClass,
  }
}
